import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import AdminLayout from '../layout/AdminLayout';
import BotSelector from '../layout/BotSelector';
import PushTopicForm from './PushTopicForm';
import { listBots, Bot } from '../../services/adminApiService';
import './PushTopicPage.css';

const SESSION_STORAGE_KEY = 'selectedBotId';

const PushTopicPage: React.FC = () => {
  const { t } = useTranslation();
  const [bots, setBots] = useState<Bot[]>([]);
  const [botId, setBotId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchBots = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await listBots();
        setBots(response.bots);

        if (response.bots.length > 0) {
          // Try to restore from session, otherwise use first bot
          const savedBotId = sessionStorage.getItem(SESSION_STORAGE_KEY);
          const validSavedBot = response.bots.find(b => b.botId === savedBotId);
          setBotId(validSavedBot ? savedBotId : response.bots[0].botId);
        } else {
          setError(t('pushTopic.noBots'));
        }
      } catch (err) {
        console.error('Error fetching bots:', err);
        setError(t('pushTopic.errorLoadingBot'));
      } finally {
        setLoading(false);
      }
    };

    fetchBots();
  }, [t]);

  const handleBotSelect = (newBotId: string) => {
    setBotId(newBotId);
    sessionStorage.setItem(SESSION_STORAGE_KEY, newBotId);
  };

  const showBotSelector = bots.length > 1;  // Hide if only one bot

  return (
    <AdminLayout>
      <div className="push-topic-page">
        <div className="page-header">
          <h2 className="page-title">{t('pushTopic.title')}</h2>
          <p className="page-description">{t('pushTopic.description')}</p>
        </div>

        {/* Loading state */}
        {loading && (
          <div className="loading-container">
            <p>{t('pushTopic.loadingBot')}</p>
          </div>
        )}

        {/* Error state */}
        {error && !loading && (
          <div className="error-container">
            <p className="error-message">{error}</p>
          </div>
        )}

        {/* Bot Selector - only show if multiple bots available */}
        {!loading && !error && showBotSelector && (
          <div className="bot-selector-container">
            <BotSelector
              bots={bots}
              selectedBotId={botId}
              onBotSelect={handleBotSelect}
            />
          </div>
        )}

        {/* Form - show when bot is selected */}
        {botId && !loading && !error && (
          <div className="push-topic-form-container">
            <PushTopicForm botId={botId} />
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default PushTopicPage;
